import { getAuthHeaders } from './auth';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export const getPresignedUploadUrl = async (filename, contentType) => {
  const response = await fetch(`${API_URL}/api/photos/presign`, {
    method: 'POST',
    headers: getAuthHeaders(),
    body: JSON.stringify({ filename, content_type: contentType })
  });
  
  if (!response.ok) {
    let errorMessage = 'Failed to get upload URL';
    try {
      const error = await response.json();
      errorMessage = error.detail || errorMessage;
    } catch (e) { /* parse error */ }
    throw new Error(errorMessage);
  }
  
  return response.json();
};

export const uploadToS3 = async (uploadUrl, file) => {
  const response = await fetch(uploadUrl, {
    method: 'PUT',
    headers: { 'Content-Type': file.type },
    body: file
  });
  
  if (!response.ok) {
    throw new Error('Failed to upload to S3');
  }
};

export const commitPhoto = async (s3Key, filename, latitude, longitude, attributes = null, skipFaceScan = false) => {
  const payload = {
    s3_key: s3Key,
    filename,
    latitude,
    longitude,
    skip_face_scan: skipFaceScan
  };
  
  if (attributes) {
    payload.attributes = attributes;
  }
  
  const response = await fetch(`${API_URL}/api/photos/commit`, {
    method: 'POST',
    headers: getAuthHeaders(),
    body: JSON.stringify(payload)
  });
  
  if (!response.ok) {
    let errorMessage = 'Failed to commit photo';
    try {
      const error = await response.json();
      errorMessage = error.detail || errorMessage;
    } catch (e) { /* parse error */ }
    throw new Error(errorMessage);
  }
  
  return response.json();
};

export const confirmShares = async (photoId, recipientIds) => {
  const response = await fetch(`${API_URL}/api/photos/${photoId}/confirm-shares`, {
    method: 'POST',
    headers: getAuthHeaders(),
    body: JSON.stringify({ recipient_ids: recipientIds })
  });
  
  if (!response.ok) {
    let errorMessage = 'Failed to confirm shares';
    try {
      const error = await response.json();
      errorMessage = error.detail || errorMessage;
    } catch (e) { /* parse error */ }
    throw new Error(errorMessage);
  }
  
  return response.json();
};

export const skipReview = async (photoId) => {
  const response = await fetch(`${API_URL}/api/photos/${photoId}/skip-review`, {
    method: 'POST',
    headers: getAuthHeaders()
  });
  
  if (!response.ok) {
    let errorMessage = 'Failed to skip review';
    try {
      const error = await response.json();
      errorMessage = error.detail || errorMessage;
    } catch (e) { /* parse error */ }
    throw new Error(errorMessage);
  }
  
  return response.json();
};

export const getFeed = async (skip = 0, limit = 20, locationSearch = null) => {
  let url = `${API_URL}/api/feed?skip=${skip}&limit=${limit}`;
  if (locationSearch && locationSearch.trim()) {
    url += `&location_search=${encodeURIComponent(locationSearch.trim())}`;
  }
  
  const response = await fetch(url, {
    headers: getAuthHeaders()
  });
  
  if (!response.ok) {
    throw new Error('Failed to get feed');
  }
  
  return response.json();
};

export const getLocationSuggestions = async (query) => {
  if (!query || query.trim().length < 1) {
    return { suggestions: [] };
  }
  
  const response = await fetch(
    `${API_URL}/api/feed/location-suggestions?q=${encodeURIComponent(query.trim())}`,
    { headers: getAuthHeaders() }
  );
  
  if (!response.ok) {
    return { suggestions: [] };
  }
  
  return response.json();
};

export const deletePhoto = async (photoId) => {
  const response = await fetch(`${API_URL}/api/photos/${photoId}`, {
    method: 'DELETE',
    headers: getAuthHeaders()
  });
  
  if (!response.ok) {
    let errorMessage = 'Failed to delete photo';
    try {
      const error = await response.json();
      errorMessage = error.detail || errorMessage;
    } catch (e) { /* parse error */ }
    throw new Error(errorMessage);
  }
  
  return response.json();
};

// Access Requests API
export const createAccessRequest = async (photoId) => {
  const response = await fetch(`${API_URL}/api/access-requests/${photoId}`, {
    method: 'POST',
    headers: getAuthHeaders()
  });
  
  if (!response.ok) {
    let errorMessage = 'Failed to send request';
    try {
      const error = await response.json();
      errorMessage = error.detail || errorMessage;
    } catch (e) { /* parse error */ }
    throw new Error(errorMessage);
  }
  
  return response.json();
};

export const getAccessRequests = async (statusFilter = 'pending') => {
  const response = await fetch(`${API_URL}/api/access-requests?status_filter=${statusFilter}`, {
    headers: getAuthHeaders()
  });
  
  if (!response.ok) {
    throw new Error('Failed to get access requests');
  }
  
  return response.json();
};

export const approveAccessRequest = async (requestId) => {
  const response = await fetch(`${API_URL}/api/access-requests/${requestId}/approve`, {
    method: 'PUT',
    headers: getAuthHeaders()
  });
  
  if (!response.ok) {
    let errorMessage = 'Failed to approve request';
    try {
      const error = await response.json();
      errorMessage = error.detail || errorMessage;
    } catch (e) { /* parse error */ }
    throw new Error(errorMessage);
  }
  
  return response.json();
};

export const denyAccessRequest = async (requestId) => {
  const response = await fetch(`${API_URL}/api/access-requests/${requestId}/deny`, {
    method: 'PUT',
    headers: getAuthHeaders()
  });
  
  if (!response.ok) {
    let errorMessage = 'Failed to deny request';
    try {
      const error = await response.json();
      errorMessage = error.detail || errorMessage;
    } catch (e) { /* parse error */ }
    throw new Error(errorMessage);
  }
  
  return response.json();
};
